/**
 * What has shipped, in the order the specification sequences it.
 *
 * **The only place this is written down.** It began as one array on the
 * dashboard, then got copied to the marketing page and again to the sign-in
 * panel — and both copies went stale the moment a phase shipped, which is the
 * exact failure the first array was meant to prevent. Three lists that have to
 * agree is not one list.
 *
 * Shipping a phase is flipping `done` here. Nothing else.
 */
export type Phase = {
  /** 0–5, matching the specification's own numbering. */
  number: number;
  /** The short name, used on the marketing page. */
  name: string;
  detail: string;
  done: boolean;
  /** Where the module lives, for the dashboard to link to once it is built. */
  to?: string;
  permission?: string;
};

export const PHASES: Phase[] = [
  {
    number: 0,
    name: 'Foundation',
    detail: 'Auth, users, roles and permissions, teams, audit log, files, notifications, search',
    done: true,
  },
  {
    number: 1,
    name: 'CRM core',
    detail: 'Leads, companies, contacts, pipeline, deals, quotes, activities',
    done: true,
    to: '/crm/deals',
    permission: 'crm.deal.view',
  },
  {
    number: 2,
    name: 'Finance',
    detail: 'Products, subscriptions, invoices, payments, expenses, ledger, dashboard',
    done: true,
    to: '/finance',
    permission: 'finance.invoice.view',
  },
  {
    number: 3,
    name: 'Delivery & service',
    detail: 'Projects, sprints, timesheets, tickets with SLA, AMC contracts',
    done: true,
    to: '/delivery/projects',
    permission: 'project.project.view',
  },
  {
    number: 4,
    name: 'Documents & marketing',
    detail: 'Document vault, e-signature, email and WhatsApp campaigns',
    done: true,
    to: '/documents',
    permission: 'file.document.view',
  },
  {
    number: 5,
    name: 'Portal & forecasting',
    detail: 'Client portal, cash-flow forecasting, report builder, workflow rules',
    done: true,
    to: '/insights',
    permission: 'report.dashboard.view',
  },
];

/** "Phase 2 — Finance", the long form the dashboard uses. */
export function phaseTitle(phase: Phase): string {
  return `Phase ${phase.number} — ${phase.name}`;
}

export const shippedPhases = PHASES.filter((phase) => phase.done);
export const nextPhase = PHASES.find((phase) => !phase.done);

/** "4 of 6" — the count the sign-in panel and both roadmaps quote. */
export const phaseCountLabel = `${shippedPhases.length} of ${PHASES.length}`;
